import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { getApiUrl } from '../utils/env';
import authService from '../services/AuthService';

interface AgrarmonitorSetupDialogProps {
  open: boolean;
  onClose: () => void;
}

interface AgrarmonitorStatus {
  freigeschaltet: boolean;
  status?: number;
  redirected?: boolean;
  redirectLocation?: string;
  timestamp?: string;
  error?: string;
}

interface AgrarmonitorRegistrationStatus {
  registriert: boolean;
  status?: number;
  hasRegistrationText?: boolean;
  timestamp?: string;
  error?: string;
}

interface AgrarmonitorRegistrationResult {
  success: boolean;
  status?: number;
  message?: string;
  data?: {
    agrarmonitorId: string;
    pcName: string;
    nonce?: string;
  };
  timestamp?: string;
  error?: string;
}

interface AgrarmonitorSetup {
  _id?: string;
  agrarmonitorId: string;
  pcName: string;
  username: string;
  password: string;
  hasPassword?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

const AgrarmonitorSetupDialog: React.FC<AgrarmonitorSetupDialogProps> = ({
  open,
  onClose
}) => {
  const [agrarmonitorStatus, setAgrarmonitorStatus] = useState<AgrarmonitorStatus | null>(null);
  const [registrationStatus, setRegistrationStatus] = useState<AgrarmonitorRegistrationStatus | null>(null);
  const [setupData, setSetupData] = useState<AgrarmonitorSetup>({
    agrarmonitorId: '',
    pcName: '',
    username: '',
    password: ''
  });
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationResult, setRegistrationResult] = useState<AgrarmonitorRegistrationResult | null>(null);
  const [registrationPerformLoading, setRegistrationPerformLoading] = useState(false);

  const checkRegistrationStatus = async () => {
    setRegistrationLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${getApiUrl()}/agrarmonitor/registriert`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await authService.getAccessTokenAsync()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Fehler: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setRegistrationStatus(data);
      
      // Wenn registriert, dann auch Freischaltungsstatus prüfen
      if (data.registriert) {
        await checkAgrarmonitorStatus();
      } else {
        // Wenn nicht registriert, Freischaltungsstatus zurücksetzen
        setAgrarmonitorStatus(null);
      }
      
    } catch (error) {
      console.error('❌ Fehler beim Prüfen der Agrarmonitor-Registrierung:', error);
      setError(error instanceof Error ? error.message : 'Unbekannter Fehler');
      setRegistrationStatus({
        registriert: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      });
    } finally {
      setRegistrationLoading(false);
    }
  };

  const checkAgrarmonitorStatus = async () => {
    setStatusLoading(true);
    
    try {
      const response = await fetch(`${getApiUrl()}/agrarmonitor/freigeschaltet`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await authService.getAccessTokenAsync()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Fehler: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setAgrarmonitorStatus(data);
      
    } catch (error) {
      console.error('❌ Fehler beim Prüfen der Agrarmonitor-Freischaltung:', error);
      setAgrarmonitorStatus({
        freigeschaltet: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const loadSetupData = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/agrarmonitor/setup`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await authService.getAccessTokenAsync()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Fehler: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setSetupData(data);
      
    } catch (error) {
      console.error('❌ Fehler beim Laden der Setup-Daten:', error);
      setSetupError(error instanceof Error ? error.message : 'Unbekannter Fehler');
    }
  };

  const saveSetupData = async () => {
    if (!setupData.agrarmonitorId.trim() || !setupData.pcName.trim()) {
      setSetupError('Bitte füllen Sie alle Felder aus');
      return;
    }

    setSetupLoading(true);
    setSetupError(null);
    
    try {
      const response = await fetch(`${getApiUrl()}/agrarmonitor/setup`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await authService.getAccessTokenAsync()}`,
        },
        body: JSON.stringify({
          agrarmonitorId: setupData.agrarmonitorId,
          pcName: setupData.pcName,
          username: setupData.username,
          password: setupData.password
        }),
      });

      if (!response.ok) {
        throw new Error(`Fehler: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setSetupData(data.setup);
      
    } catch (error) {
      console.error('❌ Fehler beim Speichern der Setup-Daten:', error);
      setSetupError(error instanceof Error ? error.message : 'Unbekannter Fehler');
    } finally {
      setSetupLoading(false);
    }
  };

  const performRegistration = async () => {
    setRegistrationPerformLoading(true);
    setRegistrationResult(null);
    
    try {
      const response = await fetch(`${getApiUrl()}/agrarmonitor/registrieren`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await authService.getAccessTokenAsync()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Fehler: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setRegistrationResult(data);
      
      // Nach erfolgreicher Registrierung beide Status neu prüfen
      if (data.success) {
        await checkRegistrationStatus();
        await checkAgrarmonitorStatus();
      }
      
    } catch (error) {
      console.error('❌ Fehler bei der Registrierung:', error);
      setRegistrationResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      });
    } finally {
      setRegistrationPerformLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      checkRegistrationStatus();
      loadSetupData();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const getRegistrationStatusColor = () => {
    if (registrationLoading) return 'default';
    if (error || !registrationStatus) return 'error';
    return registrationStatus.registriert ? 'success' : 'warning';
  };

  const getRegistrationStatusIcon = () => {
    if (registrationLoading) return <CircularProgress size={20} />;
    if (error || !registrationStatus) return <ErrorIcon />;
    return registrationStatus.registriert ? <CheckCircleIcon /> : <ErrorIcon />;
  };

  const getRegistrationStatusText = () => {
    if (registrationLoading) return 'Prüfe Registrierung...';
    if (error) return 'Fehler beim Prüfen';
    if (!registrationStatus) return 'Unbekannter Status';
    return registrationStatus.registriert ? 'Registriert' : 'Nicht registriert';
  };

  const getStatusColor = () => {
    if (statusLoading) return 'default';
    if (!agrarmonitorStatus) return 'default';
    return agrarmonitorStatus.freigeschaltet ? 'success' : 'warning';
  };

  const getStatusIcon = () => {
    if (statusLoading) return <CircularProgress size={20} />;
    if (!agrarmonitorStatus) return <ErrorIcon />;
    return agrarmonitorStatus.freigeschaltet ? <CheckCircleIcon /> : <ErrorIcon />;
  };

  const getStatusText = () => {
    if (statusLoading) return 'Prüfe Freischaltung...';
    if (!agrarmonitorStatus) return 'Nicht geprüft';
    return agrarmonitorStatus.freigeschaltet ? 'Freigeschaltet' : 'Nicht freigeschaltet';
  };

  // Bestimme ob Felder bearbeitbar sind (nur wenn nicht registriert)
  const isFieldsEditable = registrationStatus ? !registrationStatus.registriert : false;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🔧 Agrarmonitor-Setup
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Registrierungsstatus */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Registrierungsstatus
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip
                icon={getRegistrationStatusIcon()}
                label={getRegistrationStatusText()}
                color={getRegistrationStatusColor()}
                variant="outlined"
              />
              
              <Button
                startIcon={<RefreshIcon />}
                onClick={checkRegistrationStatus}
                disabled={registrationLoading}
                size="small"
                variant="outlined"
              >
                Aktualisieren
              </Button>
            </Box>

            {registrationStatus && !registrationLoading && (
              <Box sx={{ mt: 2 }}>
                {registrationStatus.registriert ? (
                  <Alert severity="success">
                    ✅ Agrarmonitor ist registriert.
                    <br />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Status-Code: {registrationStatus.status || 'N/A'}
                      {registrationStatus.timestamp && (
                        <><br />Geprüft: {new Date(registrationStatus.timestamp).toLocaleString()}</>
                      )}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    ⚠️ Agrarmonitor ist nicht registriert.
                    <br />
                    {registrationStatus.hasRegistrationText && (
                      <>Registrierungstext "Neues Gerät registrieren" wurde gefunden.</>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Status-Code: {registrationStatus.status || 'N/A'}
                      {registrationStatus.timestamp && (
                        <><br />Geprüft: {new Date(registrationStatus.timestamp).toLocaleString()}</>
                      )}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                ❌ Fehler beim Prüfen der Registrierung: {error}
              </Alert>
            )}
          </Box>

          {/* Freischaltungsstatus - nur anzeigen wenn registriert */}
          {registrationStatus?.registriert && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Freischaltungsstatus
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip
                    icon={getStatusIcon()}
                    label={getStatusText()}
                    color={getStatusColor()}
                    variant="outlined"
                  />
                  
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={checkAgrarmonitorStatus}
                    disabled={statusLoading}
                    size="small"
                    variant="outlined"
                  >
                    Aktualisieren
                  </Button>
                </Box>

                {agrarmonitorStatus && !statusLoading && (
                  <Box sx={{ mt: 2 }}>
                    {agrarmonitorStatus.freigeschaltet ? (
                      <Alert severity="success">
                        ✅ Agrarmonitor ist freigeschaltet und einsatzbereit.
                        <br />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Status-Code: {agrarmonitorStatus.status || 'N/A'}
                          {agrarmonitorStatus.timestamp && (
                            <><br />Geprüft: {new Date(agrarmonitorStatus.timestamp).toLocaleString()}</>
                          )}
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="warning">
                        ⚠️ Agrarmonitor ist nicht freigeschaltet.
                        <br />
                        {agrarmonitorStatus.redirected && (
                          <>Die Anfrage wurde zur Freischaltungsseite weitergeleitet.</>
                        )}
                        {agrarmonitorStatus.redirectLocation && (
                          <><br />Weiterleitung zu: {agrarmonitorStatus.redirectLocation}</>
                        )}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Status-Code: {agrarmonitorStatus.status || 'N/A'}
                          {agrarmonitorStatus.timestamp && (
                            <><br />Geprüft: {new Date(agrarmonitorStatus.timestamp).toLocaleString()}</>
                          )}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
            </>
          )}

          <Divider />

          {/* Setup-Daten */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Setup-Daten
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="AgrarmonitorID"
                value={setupData.agrarmonitorId}
                onChange={(e) => setSetupData({ ...setupData, agrarmonitorId: e.target.value })}
                fullWidth
                variant="outlined"
                helperText={isFieldsEditable 
                  ? "Eindeutige ID für Ihren Agrarmonitor-Zugang" 
                  : "Felder sind schreibgeschützt da Agrarmonitor bereits registriert ist"
                }
                disabled={setupLoading || !isFieldsEditable}
              />
              
              <TextField
                label="PC-Name"
                value={setupData.pcName}
                onChange={(e) => setSetupData({ ...setupData, pcName: e.target.value })}
                fullWidth
                variant="outlined"
                helperText={isFieldsEditable 
                  ? "Name des Computers/Clients für Agrarmonitor"
                  : "Felder sind schreibgeschützt da Agrarmonitor bereits registriert ist"
                }
                disabled={setupLoading || !isFieldsEditable}
              />

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Anmeldedaten
                </Typography>
              </Divider>

              <TextField
                label="Benutzername"
                value={setupData.username}
                onChange={(e) => setSetupData({ ...setupData, username: e.target.value })}
                fullWidth
                variant="outlined"
                helperText={isFieldsEditable 
                  ? "Ihr Agrarmonitor-Benutzername"
                  : "Felder sind schreibgeschützt da Agrarmonitor bereits registriert ist"
                }
                disabled={setupLoading || !isFieldsEditable}
              />

              <TextField
                label="Passwort"
                type="password"
                value={setupData.password}
                onChange={(e) => setSetupData({ ...setupData, password: e.target.value })}
                fullWidth
                variant="outlined"
                helperText={isFieldsEditable 
                  ? (setupData.hasPassword && !setupData.password ? "Aktuelles Passwort ist gesetzt - leer lassen um beizubehalten" : "Ihr Agrarmonitor-Passwort")
                  : "Felder sind schreibgeschützt da Agrarmonitor bereits registriert ist"
                }
                disabled={setupLoading || !isFieldsEditable}
                placeholder={setupData.hasPassword ? "••••••••" : ""}
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  startIcon={setupLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={saveSetupData}
                  disabled={setupLoading || !setupData.agrarmonitorId.trim() || !setupData.pcName.trim() || !isFieldsEditable}
                  variant="contained"
                  color="primary"
                >
                  {setupLoading ? 'Speichere...' : 'Speichern'}
                </Button>
                
                {setupData.updatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Zuletzt aktualisiert: {new Date(setupData.updatedAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
              
              {setupError && (
                <Alert severity="error">
                  ❌ {setupError}
                </Alert>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Registrierung Durchführen */}
          {!registrationStatus?.registriert && setupData.agrarmonitorId && setupData.pcName && (
            <>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Registrierung Durchführen
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Führen Sie die Registrierung bei Agrarmonitor durch.
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Button
                    startIcon={registrationPerformLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    onClick={performRegistration}
                    disabled={registrationPerformLoading}
                    variant="contained"
                    color="success"
                  >
                    {registrationPerformLoading ? 'Registriere...' : 'Jetzt Registrieren'}
                  </Button>
                </Box>
                
                {registrationResult && (
                  <Box sx={{ mt: 2 }}>
                    {registrationResult.success ? (
                      <Alert severity="success">
                        ✅ Registrierung erfolgreich!
                        <br />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Status-Code: {registrationResult.status || 'N/A'}
                          {registrationResult.data && (
                            <>
                              <br />AgrarmonitorID: {registrationResult.data.agrarmonitorId}
                              <br />PC-Name: {registrationResult.data.pcName}
                            </>
                          )}
                          {registrationResult.timestamp && (
                            <><br />Zeitstempel: {new Date(registrationResult.timestamp).toLocaleString()}</>
                          )}
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="error">
                        ❌ Registrierung fehlgeschlagen: {registrationResult.message || registrationResult.error}
                        <br />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {registrationResult.status && <>Status-Code: {registrationResult.status}<br /></>}
                          {registrationResult.timestamp && (
                            <>Zeitstempel: {new Date(registrationResult.timestamp).toLocaleString()}</>
                          )}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
              
              <Divider />
            </>
          )}

          <Divider />

          {/* Readonly-Einstellung */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Kontakt-Einstellungen
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={true}
                  disabled={true}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Agrarmonitor-Kontakte als schreibgeschützt markieren
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Diese Einstellung ist aktiviert und kann nicht geändert werden.
                    Agrarmonitor-Kontakte sind immer schreibgeschützt.
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Informationen */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Hinweise
            </Typography>
            
            <Alert severity="info">
              <Typography variant="body2">
                • Zuerst wird die Registrierung geprüft
                <br />
                • Setup-Daten können nur bearbeitet werden wenn nicht registriert
                <br />
                • Bei erfolgreicher Registrierung wird automatisch die Freischaltung geprüft
                <br />
                • Agrarmonitor-Kontakte werden automatisch über die API synchronisiert
              </Typography>
            </Alert>
          </Box>

        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgrarmonitorSetupDialog;
